import React, { useState, useRef, useEffect } from 'react';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';
import { useParams } from 'react-router-dom';
import { useNavigateWithinProject } from '../utils/navigation';

interface VersionSwitcherProps {
    detailView: 'pack copy' | 'label';
}

const VersionSwitcher: React.FC<VersionSwitcherProps> = ({ detailView }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const {
        state: { labels, packCopies },
    } = useProjectDetailsContext();

    const navigate = useNavigateWithinProject();

    // Determine available versions based on detail view
    const versions =
        detailView === 'pack copy'
            ? packCopies.map((packCopy) => ({
                  id: packCopy.id,
                  name: packCopy.name,
              }))
            : labels.map((v) => ({
                  id: v.id,
                  name: v.name,
              })) || [];

    const { labelId, packCopyId } = useParams<{
        labelId: string;
        packCopyId: string;
    }>();

    const currentVersion =
        detailView === 'pack copy'
            ? packCopies.find((packCopy) => packCopy.id === packCopyId)?.name
            : labels.find((label) => label.id === labelId)?.name;

    // Handle clicks outside the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        // Add event listener when dropdown is open
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Clean up the event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const selectVersion = (versionId: string) => {
        if (detailView === 'pack copy') {
            if (packCopyId === versionId) {
                return;
            }
            navigate(`/pack-copies/${versionId}`);
        } else {
            if (labelId === versionId) {
                return;
            }
            navigate(`/labels/${versionId}`);
        }
        setIsOpen(false);
    };
    console.log(currentVersion);

    return (
        <div className="version-switcher" ref={dropdownRef}>
            <button
                onClick={toggleDropdown}
                className="version-switcher-button"
            >
                <span>{currentVersion}</span>
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M8.25 9 L12 5.25 L15.75 9 M8.25 15 L12 18.75 L15.75 15"
                        stroke="#0F172A"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {isOpen && (
                <div className="version-dropdown">
                    {versions.length > 0 ? (
                        versions.map((version) => (
                            <button
                                key={version.id}
                                onClick={() => selectVersion(version.id)}
                                className={
                                    currentVersion === version.name
                                        ? 'active'
                                        : ''
                                }
                            >
                                {version.name}
                            </button>
                        ))
                    ) : (
                        <div className="no-versions">No versions available</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VersionSwitcher;
